import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, ArrowUpCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { waeAPI, STATUS_STYLE, STATUS_LABEL, PRIORITY_STYLE, fmtDT } from "./waeApi";
import { EmptyState } from "./uiKit";
import RequestDrawer from "./RequestDrawer";

export default function WAEEscalations() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [sweeping, setSweeping] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = () => waeAPI.requests({ status: "escalated", limit: 200 })
                        .then(setRows).catch(()=>{});
  useEffect(() => { load(); }, []);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const r = await waeAPI.sweep();
      toast.success(`SLA sweep: ${r.escalated} escalated`);
      load();
    } catch { toast.error("Sweep failed"); }
    finally { setSweeping(false); }
  };

  return (
    <div className="space-y-4" data-testid="wae-escalations">
      <div className="flex items-center gap-3">
        <div className="text-[13px] text-slate-500">
          SLA-driven auto-escalation. Any request past its current level SLA is auto-escalated to the level&apos;s <span className="mono">escalate_to</span> approver or the requester&apos;s manager.
        </div>
        <button onClick={runSweep} disabled={sweeping} data-testid="wae-escal-sweep"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${sweeping ? "animate-spin" : ""}`}/> Run SLA Sweep Now
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {rows.length === 0
          ? <EmptyState icon={ArrowUpCircle} title="Nothing escalated"
              msg="All active workflows are within SLA. Nice."/>
          : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Subject</th>
                  <th className="text-left px-4 py-3">Requester</th>
                  <th className="text-center px-4 py-3">Priority</th>
                  <th className="text-left px-4 py-3">Stage</th>
                  <th className="text-left px-4 py-3">Escalated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => (
                  <tr key={r.id} onClick={()=>setOpenId(r.id)} data-testid={`wae-escal-row-${r.id}`}
                      className="cursor-pointer hover:bg-slate-50/70 bg-amber-50/40">
                    <td className="px-4 py-3 mono font-medium text-hocp-navy">{r.code}</td>
                    <td className="px-4 py-3 max-w-xs truncate">{r.subject}</td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">{r.requester_email}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</span>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {(r.levels || [])[r.current_level_index]?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-[12px] mono text-slate-500">
                      <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3 h-3"/>{fmtDT(r.escalated_at || r.updated_at)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      {openId && <RequestDrawer requestId={openId} currentUserId={user?.id || user?._id}
                                  onClose={()=>setOpenId(null)} onChanged={load}/>}
    </div>
  );
}
